import { FC, useEffect } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  gridSize,
  viewMorePopOverGridSize,
} from "../../../../../../helpers/constants";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { ViewProjectInvoiceWrapper } from "./ViewProjectInvoiceDetails.style";
import { ViewProjectInvoiceFeesAndExpensesList } from "./ViewProjectInvoiceFeesAndExpensesLists";

import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetProjectInvoiceByIdMutation,
  useGetProjectInvoicingLanguages,
} from "../../../../../../api/services/project/projectApis";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  formatDate,
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getValueOrBlank,
  getValueOrDash,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import { currencyListSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { projectInvoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

interface ViewProjectInvoiceProps {
  invoiceId: string;
  projectId: string;
}

export const ViewProjectInvoice: FC<ViewProjectInvoiceProps> = ({
  invoiceId,
  projectId,
}) => {
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();

  const {
    mutate,
    data: invoiceResponse,
    isPending: isGetInvoicePending,
  } = useGetProjectInvoiceByIdMutation();

  const dispatch = useDispatch();

  const { loggedInUser } = useGetLoggedInUserDetails();

  const {
    data: projectInvoicingLanguages,
    refetchWithParams: refetchProjectInvoicingLanguages,
  } = useGetProjectInvoicingLanguages();

  useEffect(() => {
    if (invoiceId) {
      mutate(invoiceId);
    }

    if (projectId) {
      refetchProjectInvoicingLanguages({
        projectId,
      });
    }
  }, [invoiceId, projectId, mutate]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isGetInvoicePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isGetInvoicePending]);

  const getProjectLabel = (
    key: keyof typeof projectInvoiceHistorySiteCoreKeys,
  ) => {
    return fetchLabel(
      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      projectInvoiceHistorySiteCoreKeys[key],
    );
  };

  const getCurrencyName = () => {
    return invoiceResponse?.feeCurrency?.name ?? "";
  };

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const currencyList = useSelector(currencyListSelector);

  const getFormattedValue = (value: number | undefined): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      invoiceResponse?.feeCurrency?.id,
    );

    const formattedAmount = value
      ? `${getCurrencyName()} ${getFormattedAmount(value, currencyData?.currencyLocale)}`
      : EMPTY_VALUE_DASH;
    return formattedAmount;
  };

  return (
    <ViewProjectInvoiceWrapper>
      <MuiBox className="fields-wrapper">
        <MuiTypography variant="h3SemiBold">
          {getProjectLabel("summarySectionHeader")}
        </MuiTypography>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryTotalAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(invoiceResponse?.feeOrExpenseAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryTotalTaxAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(invoiceResponse?.totalTaxAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryTotalInvoiceAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(invoiceResponse?.totalInvoiceAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryLocalInvoiceNumber")}
              replaceEmptyWithDash
              value={getValueOrDash(invoiceResponse?.localInvoiceNumber)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={
                invoiceResponse?.statusId === STATUS_BADGE_TYPE.invoiced
                  ? getProjectLabel("summaryInvoiceDate")
                  : getProjectLabel("summaryReversedDate")
              }
              replaceEmptyWithDash
              value={formatDate(invoiceResponse?.date, userCountryDateFormat)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryPaymentTerms")}
              replaceEmptyWithDash
              value={invoiceResponse?.paymentTerm?.name}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryUtilizedPO")}
              replaceEmptyWithDash
              value={invoiceResponse?.purchaseOrders?.number || "-"}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryInvoicedStatus")}
              replaceEmptyWithDash
              value={
                invoiceResponse && (
                  <StatusBadge
                    status={invoiceResponse?.statusId}
                    label={fetchValueByKeyOfDropdown(
                      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                      projectInvoiceHistorySiteCoreKeys.invoiceStatusList,
                      invoiceResponse?.statusId,
                    )}
                  />
                )
              }
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryInvoiceLanguage")}
              replaceEmptyWithDash
              value={getValueOrBlank(projectInvoicingLanguages?.[0]?.name)}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      {invoiceResponse?.reversalReason && (
        <MuiGrid container spacing={2} className="reversal-reason">
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getProjectLabel("summaryReversalReason")}
              replaceEmptyWithDash
              value={fetchValueByKeyOfDropdown(
                projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                projectInvoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
                invoiceResponse?.reversalReason ?? "",
              )}
            />
          </MuiGrid>
          {isNotEmpty(invoiceResponse?.reversalComments) && (
            <MuiGrid size={viewMorePopOverGridSize}>
              <MuiDoubleLabel
                label={getProjectLabel("summaryReversalComment")}
                replaceEmptyWithDash
                value={
                  invoiceResponse?.reversalComments ? (
                    <MuiViewMorePopOver
                      anchorText={getProjectLabel("viewCommentLink")}
                      message={getValueOrDash(
                        invoiceResponse?.reversalComments ?? "",
                      )}
                      placement="bottom-end"
                    />
                  ) : (
                    ""
                  )
                }
              />
            </MuiGrid>
          )}
        </MuiGrid>
      )}
      <MuiBox className="fee-and-expense-label">
        <MuiTypography variant="h3SemiBold">
          {getProjectLabel("feeAndExpensesSectionHeader")}
        </MuiTypography>
      </MuiBox>
      {invoiceResponse?.feesOrExpenses && (
        <ViewProjectInvoiceFeesAndExpensesList
          data={invoiceResponse?.feesOrExpenses}
          getFormattedValue={getFormattedValue}
        />
      )}
    </ViewProjectInvoiceWrapper>
  );
};
